import React, { useEffect, useState } from "react"
import _ from "lodash"
import moment from "moment"
import { useLocation, useHistory } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Paper, Tabs, Tab, Box, Typography } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { makeStyles, useTheme, withStyles } from "@material-ui/core/styles"
import TabPanel from "./TabPanel"
import Comments from "./Comments"
import Details from "./Details"
import AuditHistory from "./AuditHistory"
import { getAnnotation, getAuditHistory } from "../../../actions/viewerActions"
import { searchHighlightFilter } from "../../../actions/highlightActions"
import { debug } from "util"
import AiSummary from "./AiSummary"
import { checkToShowNativeFormat } from "../../../utils"

const a11yProps = (index) => {
	return {
		id: `additional-details-tab-${index}`,
		"aria-controls": `additional-details-tabpanel-${index}`,
	}
}

const useStyles = makeStyles((theme) => ({
	viewerTabs: {
		borderWidth: "1px 0px 1px 1px",
		borderStyle: "solid",
		borderColor: "rgba(44, 53, 51, 0.12)",
		"& .Mui-selected": {
			background: "rgba(52, 118, 228, 0.12)",
		},
		"& .MuiTab-root": {
			minWidth: "33.33%",
		},
	},
}))

function DetailView(props) {
	const classes = useStyles()
	const theme = useTheme()
	const location = useLocation()
	const dispatch = useDispatch()
	const history = useHistory()
	const [value, setValue] = useState(0)
	const [t] = useTranslation("global")
	const META_TRANS_KEY = "viewer.fileType.email"

	const { documentDetails, documentLoading } = useSelector((state) => state?.documentModal)
	const highlightFilter = useSelector((state) => state.highlightFilter.search)

	const handleChange = (event, newValue) => {
		setValue(newValue)
		if (newValue === 2) {
			const fileId = location?.pathname?.split("/")?.[3]?.split("?")[0]
			dispatch(getAuditHistory(fileId))
			dispatch(getAnnotation(fileId, checkToShowNativeFormat(documentDetails?.metadata)))
		}
	}

	useEffect(() => {
		if (documentDetails?.metadata) {
			dispatch(getAnnotation(location?.pathname?.split("/")?.[3]?.split("?")[0], checkToShowNativeFormat(documentDetails?.metadata)))
		}
	}, [documentDetails?.metadata])

	const formatDate = (date) => {
		if (!date) {
			return "NA"
		}
		const formate = "DD/MM/YYYY, hh:mm:ss"
		//return moment(date).format(formate)
		return props?.renderTaggedDate({ tagged_date: date })
	}

	const getDetailsTabData = () => {
		const { metadata, name, email_ref, children_files, path } = documentDetails
		if (metadata?.source === "email") {
			const detailTabData = [
				{
					title: t(`${META_TRANS_KEY}.subject`),
					value: metadata?.subject,
					isTooltip: true,
				},
				{
					title: t(`${META_TRANS_KEY}.from`),
					value: metadata?.from,
					isTooltip: false,
				},
				{
					title: t(`${META_TRANS_KEY}.to`),
					value: metadata?.to,
					isTooltip: false,
				},
			]

			metadata?.cc?.length > 0 &&
				detailTabData.push({
					title: t(`${META_TRANS_KEY}.cc`),
					value: metadata?.cc,
					isTooltip: false,
				})

			metadata?.bcc?.length > 0 &&
				detailTabData.push({
					title: t(`${META_TRANS_KEY}.bcc`),
					value: metadata?.bcc,
					isTooltip: false,
				})

			children_files?.length > 0 &&
				detailTabData.push({
					title: t(`${META_TRANS_KEY}.attachment`),
					value: children_files,
					attachment: true,
				})

			path &&
				detailTabData.push({
					title: t("viewer.metadata.pdf.file_path"),
					value: path,
					isTooltip: false,
				})

			metadata?.pii_names?.length > 0 &&
				detailTabData.push({
					title: t("viewer.metadata.pdf.ai_names"),
					value: metadata?.pii_names,
				})

			email_ref?.name &&
				detailTabData.push({
					title: t("viewer.metadata.pdf.email_ref"),
					value: email_ref?.name,
					fileId: email_ref?.file_id,
					emailRef: true,
				})

			return detailTabData
		} else {
			const detailTabData = [
				{
					title: t("viewer.metadata.pdf.file_name"),
					value: name,
					isTooltip: true,
				},
				{
					title: t("viewer.metadata.pdf.author"),
					value: "-",
					isTooltip: false,
				},
				{
					title: t("search.date"),
					value: formatDate(metadata?.tagged_date || metadata?.creation_time),
					isTooltip: false,
				},
				{
					title: t("viewer.metadata.pdf.file_path"),
					value: path,
					isTooltip: false,
				},
			]

			email_ref?.name &&
				detailTabData.push({
					title: t("viewer.metadata.pdf.email_ref"),
					value: email_ref?.name,
					fileId: email_ref?.file_id,
					emailRef: true,
				})

			metadata?.pii_names?.length > 0 &&
				detailTabData.push({
					title: t("viewer.metadata.pdf.ai_names"),
					value: metadata?.pii_names,
				})

			return detailTabData
		}
	}

	const StyledTabs = withStyles({
		indicator: {
			display: "flex",
			justifyContent: "flex-start",
			"&:focus": {
				opacity: 1,
				background: "rgba(52, 118, 228, 0.12)",
			},
			"& > span": {
				width: "100%",
				backgroundColor: theme.palette.primary.dark,
			},
			"& .MuiTab-root": {
				minWidth: "33.33%",
			},
		},
	})((props) => <Tabs {...props} variant="scrollable" scrollButtons="auto" TabIndicatorProps={{ children: <span /> }} />)

	const StyledTab = withStyles((theme) => ({
		root: {
			...theme.typography.subtitle2,
			padding: "18px 22px",
			textTransform: "none",
			color: theme.palette.text.primary,
			"& .MuiTab-root": {
				minWidth: "33.33%",
			},
		},
	}))((props) => <Tab variant="scrollable" scrollButtons="auto" disableRipple {...props} />)

	return (
		<Box>
			<Box className={classes.viewerTabs}>
				<StyledTabs value={value} onChange={handleChange}>
					<StyledTab label={t("viewer.tabs.summary.label")} {...a11yProps(0)} />
					<StyledTab label={t("viewer.tabs.comment.label")} {...a11yProps(1)} disabled={!documentDetails?.path ? true : false} />
					<StyledTab label={t("viewer.tabs.details.label")} {...a11yProps(2)} />
					<StyledTab label={t("viewer.tabs.history.label")} {...a11yProps(3)} />
				</StyledTabs>
			</Box>
			<Box>
				<TabPanel value={value} index={0}>
					<AiSummary retainedFilterJobData={props?.retainedFilterJobData} metadata={documentDetails?.metadata} />
				</TabPanel>
				<TabPanel value={value} index={1}>
					<Comments retainedFilterJobData={props?.retainedFilterJobData} documentId={documentDetails?.file_id || null} />
				</TabPanel>
				<TabPanel value={value} index={2}>
					{!documentDetails?.path ? (
						documentDetails?.error ? (
							<Typography style={{ ...theme.typography.body2 }}>{t("viewer.tabs.details.no_details")}</Typography>
						) : (
							<Skeleton variant="rect" width="auto" height="42vh" style={{ padding: "16px" }}></Skeleton>
						)
					) : (
						<Details retainedFilterJobData={props?.retainedFilterJobData} getDetailsData={getDetailsTabData} />
					)}
				</TabPanel>
				<TabPanel value={value} index={3}>
					<AuditHistory retainedFilterJobData={props?.retainedFilterJobData} />
				</TabPanel>
			</Box>
		</Box>
	)
}

export default DetailView
